import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, Subject, catchError, combineLatest, distinctUntilChanged, exhaustMap, from, map, switchMap, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ParamMapHelper } from '../helpers/param-map.helper';
import { StorageService } from './storage.service';
import { DateRangeScope } from '../types/date-range-scope.type';
import { DataAvailabilityService } from './data-availability.service';
import { DateRange } from '../types/date-range.type';
import { NavigationService } from './navigation.service';
import { DataAvailabilityInfo } from '../types/data-availability-info.type';
import { DateHelper, DateRangeFormat } from '../helpers/date.helper';
import { ApiService } from './api/api.service';
import { SharedTimeUpdateService } from './shared-time-update.service';

@Injectable({ providedIn: 'root' })
export class CalendarService {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly storageService = inject(StorageService);
  private readonly dataAvailabilityService = inject(DataAvailabilityService);
  private readonly navigationService = inject(NavigationService);
  private readonly apiService = inject(ApiService);
  private readonly sharedTimeUpdateService = inject(SharedTimeUpdateService);

  private dateRangeScope: DateRangeScope = DateRangeScope.Global;
  private getDatesWithData: () => Promise<DateRange[]>;
  private getDataAvailabilityInfo: () => Promise<DataAvailabilityInfo>;
  private _dateRange: DateRange;
  private _dataAvailability: DataAvailabilityInfo;
  private today: Date;
  // Incremented only by initializeCalendar. Used to prevent stale async work from publishing state.
  private initVersion = 0;
  private readonly TODAY_DATA_RECHECK_MS = 5 * 60 * 1000;
  private readonly dateRangeChangedSubject = new Subject<DateRange>();
  private readonly dateChangedSubject = new Subject<Date>();
  private readonly dataAvailabilityChangedSubject = new Subject<DataAvailabilityInfo>();
  private readonly waitingForTodayData$ = new BehaviorSubject<boolean>(false);
  private readonly isCalendarActive$ = new BehaviorSubject<boolean>(false);

  get dateRange(): DateRange {
    return this._dateRange;
  }

  get fullDateRange(): DateRange {
    if (this._dateRange == null) {
      return null;
    }
    return new DateRange(DateHelper.startOfDay(this._dateRange.start), DateHelper.endOfDay(this._dateRange.end));
  }

  get date(): Date {
    return this._dateRange?.start;
  }

  get dateRangeChanged$(): Observable<DateRange> {
    return this.dateRangeChangedSubject.asObservable();
  }

  get dateChanged$(): Observable<Date> {
    return this.dateChangedSubject.asObservable();
  }

  get dataAvailability(): DataAvailabilityInfo {
    return this._dataAvailability;
  }

  get dataAvailabilityChanged$(): Observable<DataAvailabilityInfo> {
    return this.dataAvailabilityChangedSubject.asObservable();
  }

  get dateRangeLabel(): string {
    if (this.dateRange == null) {
      return '';
    }
    return this.dateRange.isSingleDay() ? DateHelper.getDayLabel(this.dateRange.start) : this.dateRange.format(DateRangeFormat.DateOnly);
  }

  constructor() {
    this.watchDayRollover();
    this.watchTodayDataAvailability();
  }

  private watchDayRollover(): void {
    this.sharedTimeUpdateService.timeUpdates$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap(() => from(this.checkDateChange()).pipe(catchError(() => EMPTY))),
      )
      .subscribe();
  }

  activate(): void {
    this.isCalendarActive$.next(true);
  }

  deactivate(): void {
    this.isCalendarActive$.next(false);
  }

  private watchTodayDataAvailability(): void {
    combineLatest([this.waitingForTodayData$, this.isCalendarActive$]).pipe(
      takeUntilDestroyed(),
      map(([waiting, active]) => waiting && active),
      distinctUntilChanged(),
      switchMap(shouldPoll => shouldPoll ? timer(0, this.TODAY_DATA_RECHECK_MS) : EMPTY),
      // A new timer tick is ignored until the previous poll completes.
      exhaustMap(() => from(this.pollTodayAvailability()).pipe(catchError(() => EMPTY))),
    ).subscribe();
  }

  private async checkDateChange(): Promise<void> {
    const today = DateHelper.startOfToday();
    if (this.today == null) {
      return;
    }
    if (this.today.getTime() !== today.getTime()) {
      this.dataAvailabilityService.clear();
      await this.initializeCalendar(this.dateRangeScope, this.getDatesWithData, this.getDataAvailabilityInfo);
    }
  }

  private async pollTodayAvailability(): Promise<void> {
    const capturedInitVersion = this.initVersion;
    this.dataAvailabilityService.invalidate();
    const availability = await this.getDataAvailability();
    if (!this.isCurrentInitVersion(capturedInitVersion)) {
      return;
    }
    this.publishAvailability(availability);
    if (this.hasDataForToday(availability)) {
      if (this._dateRange?.endsToday()) {
        this.dateRangeChangedSubject.next(this._dateRange);
        this.dateChangedSubject.next(this._dateRange.start);
      }
    }
  }

  public async initializeCalendar(
    dateRangeScope: DateRangeScope = DateRangeScope.Global,
    getDatesWithData?: () => Promise<DateRange[]>,
    getDataAvailabilityInfo?: () => Promise<DataAvailabilityInfo>,
    forcedDateRange?: DateRange,
  ): Promise<void> {
    this.today = DateHelper.startOfToday();
    ++this.initVersion;
    const capturedVersion = this.initVersion;
    if (this.dateRangeScope != dateRangeScope) {
      this.dateRangeScope = dateRangeScope;
      this._dateRange = null;
    }
    this.getDatesWithData = getDatesWithData;
    this.getDataAvailabilityInfo = getDataAvailabilityInfo;
    if (!await this.fetchAndPublishAvailability(capturedVersion)) {
      return;
    }
    await this.resolveAndSelectDateRange(forcedDateRange, capturedVersion);
  }

  private async fetchAndPublishAvailability(capturedVersion: number): Promise<boolean> {
    const availability = await this.getDataAvailability();
    if (!this.isCurrentInitVersion(capturedVersion)) {
      return false;
    }
    this.publishAvailability(availability);
    return true;
  }

  private async resolveAndSelectDateRange(forcedDateRange: DateRange | undefined, capturedVersion: number): Promise<void> {
    // Priority order: forcedDateRange > query params > storage > previous > data availability > today
    if (forcedDateRange?.isValid()) {
      await this.selectDateRange(forcedDateRange, capturedVersion);
      return;
    }

    const queryDateRange = ParamMapHelper.parseInterval(this.activatedRoute.snapshot.queryParamMap);
    if (queryDateRange?.isValid()) {
      await this.selectDateRange(queryDateRange, capturedVersion);
      return;
    }

    const storageDateRange = this.storageService.getDateRange(this.dateRangeScope);
    const dataAvailabilityDateRange = this._dataAvailability?.lastDate != null ? new DateRange(this._dataAvailability.lastDate) : null;
    const dateRange = storageDateRange ?? this._dateRange ?? dataAvailabilityDateRange ?? new DateRange(DateHelper.startOfToday());
    await this.selectDateRange(dateRange, capturedVersion);
  }

  private publishAvailability(availability: DataAvailabilityInfo): void {
    this._dataAvailability = availability;
    this.dataAvailabilityChangedSubject.next(availability);
    this.waitingForTodayData$.next(!this.hasDataForToday(availability));
  }

  private hasDataForToday(availability: DataAvailabilityInfo): boolean {
    return availability?.hasDataForDate(DateHelper.startOfToday()) ?? false;
  }

  private isCurrentInitVersion(expectedVersion: number): boolean {
    return this.initVersion === expectedVersion;
  }

  private async getDataAvailability(): Promise<DataAvailabilityInfo> {
    return this.getDatesWithData != null ? this.createDataAvailability(await this.getDatesWithData())
      : this.getDataAvailabilityInfo != null ? await this.getDataAvailabilityInfo()
      : this.dateRangeScope === DateRangeScope.DumpViewer ? this.createDataAvailability(await this.apiService.dumpViewer.getDatesWithData())
      : await this.dataAvailabilityService.getDataAvailability();
  }

  private createDataAvailability(datesWithData: DateRange[]): DataAvailabilityInfo {
    return new DataAvailabilityInfo(datesWithData, datesWithData.length > 0);
  }

  public async selectDateRange(dateRange: DateRange, expectedVersion?: number): Promise<void> {
    if (expectedVersion != null && !this.isCurrentInitVersion(expectedVersion)) {
      return;
    }
    this._dateRange = dateRange;
    await this.navigationService.addQueryParams(ParamMapHelper.createIntervalParams(dateRange));
    if (expectedVersion != null && !this.isCurrentInitVersion(expectedVersion)) {
      return;
    }
    this.storageService.setDateRange(this.dateRangeScope, dateRange);
    this.dateRangeChangedSubject.next(this.dateRange);
    this.dateChangedSubject.next(this.date);
  }
}
